import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import { blue, orange, red } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { BarSeriesType } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import {
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
  HarmonizedViolationStatsQuery,
} from "generated-graphql/graphql";
import { useFullOmnisearch } from "hooks/omnisearch-v2";
import { useOmnisearchActions } from "hooks/omnisearch-v2/omnisearchStore";
import { HARMONIZED_VIOLATION_STATS } from "hooks/useHarmonizedViolations";
import { ReactNode, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { formatNumber } from "util/formatNumber";
import { PriorityChartColorsPurple } from "util/insights";
import { extractOmnisearchValue } from "utils/omnisearchFilters";
import { quoteOmnisearchValue } from "utils/omnisearchUtils";

import {
  ASSIGNEE_CHART_EXPLANATION,
  BUSINESS_UNIT_CHART_EXPLANATION,
  MetricInfoIcon,
  PROGRAM_SEVERITY_EXPLANATION,
  PROGRAM_TIME_EXPLANATION,
  programChartColor,
  programIcon,
  ProgramSeverityMatrix,
  QUARTER_EXPLANATION,
  REGION_CHART_EXPLANATION,
  SEVERITY_EXPLANATION,
  STALE_EXPLANATION,
  TOP_FACILITIES_EXPLANATION,
} from "./violationDisplay";
import { ViolationKeyMetric } from "./ViolationKeyMetric";

type Stats = HarmonizedViolationStatsQuery["harmonizedViolationStats"];
type StatBucket = { key: string; label: string; count: number };

const CHART_VIEW_PARAM = "chartView";

// One entry per "View by" option, mirroring the waste page's `graphVariations`.
const chartVariations = {
  quarter: {
    selectLabel: "Quarter",
    label: "By quarter",
    explanation: QUARTER_EXPLANATION,
    infoLabel: "quarter data",
  },
  programOverTime: {
    selectLabel: "Program over time",
    label: "By program over time",
    explanation: PROGRAM_TIME_EXPLANATION,
    infoLabel: "program over time",
  },
  facility: {
    selectLabel: "Facility",
    label: "Top facilities",
    explanation: TOP_FACILITIES_EXPLANATION,
    infoLabel: "top facilities",
  },
  region: {
    selectLabel: "Region",
    label: "Open violations by Region",
    explanation: REGION_CHART_EXPLANATION,
    infoLabel: "region",
  },
  businessUnit: {
    selectLabel: "Business unit",
    label: "Open violations by Business unit",
    explanation: BUSINESS_UNIT_CHART_EXPLANATION,
    infoLabel: "business unit",
  },
  assignee: {
    selectLabel: "Assignee",
    label: "Open violations by Assignee",
    explanation: ASSIGNEE_CHART_EXPLANATION,
    infoLabel: "assignee",
  },
  programSeverity: {
    selectLabel: "Program and severity",
    label: "By program and severity",
    explanation: PROGRAM_SEVERITY_EXPLANATION,
    infoLabel: "program and severity",
  },
} as const;

type ChartViewKey = keyof typeof chartVariations;
const CHART_VIEW_KEYS = Object.keys(chartVariations) as ChartViewKey[];

function isChartViewKey(value: string | null): value is ChartViewKey {
  return CHART_VIEW_KEYS.some((key) => key === value);
}

// The resolver emits a "none" sentinel bucket for facilities without the field,
// so a view is only worth offering when a real (non-sentinel) bucket exists.
const SENTINEL_BUCKET_KEY = "none";

function hasRealBuckets(buckets: StatBucket[]): boolean {
  return buckets.some((bucket) => bucket.key !== SENTINEL_BUCKET_KEY);
}

/**
 * Legend click-to-filter for one omnisearch key: `toggle` applies, replaces, or
 * clears the value; `activeValue` drives the legend's selected-state highlight.
 * `alsoApply` pairs are written alongside on apply (not removed on clear).
 */
function useLegendFilter(key: string, alsoApply: [string, string][] = []) {
  const omnisearch = useFullOmnisearch();
  const { editKeyValues, removeKey } = useOmnisearchActions();
  const activeValue = extractOmnisearchValue(omnisearch, key);
  const isActive = (value: string) => activeValue?.toUpperCase() === value.toUpperCase();
  const toggle = (value: string) => {
    if (isActive(value)) {
      removeKey(key);
    } else {
      editKeyValues([...alsoApply, [key, quoteOmnisearchValue(value)]]);
    }
  };
  return { activeValue, isActive, toggle };
}

// The ranked group charts count UNRESOLVED violations only, so their legend
// clicks scope the page's status filter to match what the bar shows.
const APPLY_UNRESOLVED: [string, string][] = [
  ["status", HarmonizedViolationResolutionStatus.Unresolved],
];

// Softer severity palette matching the other Insights tabs; array order is the
// stacking order (High at the bottom of each bar).
const SEVERITY_STACK = [
  { key: HarmonizedViolationSeverity.High, label: "High", color: red[400] },
  { key: HarmonizedViolationSeverity.Medium, label: "Medium", color: orange[400] },
  { key: HarmonizedViolationSeverity.Low, label: "Low", color: blue[300] },
] as const;

const PROGRAM_LABELS: Record<HarmonizedViolationProgram, string> = {
  [HarmonizedViolationProgram.Water]: "Water",
  [HarmonizedViolationProgram.Air]: "Air",
  [HarmonizedViolationProgram.Waste]: "Waste",
};

const ZERO_SEVERITY_COLUMNS = {
  [HarmonizedViolationSeverity.High]: 0,
  [HarmonizedViolationSeverity.Medium]: 0,
  [HarmonizedViolationSeverity.Low]: 0,
};

const ZERO_PROGRAM_COLUMNS = {
  [HarmonizedViolationProgram.Water]: 0,
  [HarmonizedViolationProgram.Waste]: 0,
  [HarmonizedViolationProgram.Air]: 0,
};

/** Pivot flat chart cells into one dataset row per group, preserving cell order. */
function pivotCells<TCell extends { count: number }>(
  cells: TCell[],
  rowKey: (cell: TCell) => string,
  rowSeed: (cell: TCell) => Record<string, string | number>,
  column: (cell: TCell) => string
): Record<string, string | number>[] {
  const rowByKey = new Map<string, Record<string, string | number>>();
  for (const cell of cells) {
    const row = rowByKey.get(rowKey(cell)) ?? rowSeed(cell);
    row[column(cell)] = cell.count;
    rowByKey.set(rowKey(cell), row);
  }
  return Array.from(rowByKey.values());
}

// One bar series per severity, shared by the severity-stacked charts.
const SEVERITY_BAR_SERIES = SEVERITY_STACK.map(
  (severity) =>
    ({
      type: "bar",
      dataKey: severity.key,
      stack: "severity",
      label: severity.label,
      color: severity.color,
      valueFormatter: (value) => `${severity.label} (${value ?? 0})`,
    }) satisfies BarSeriesType
);

/** Clickable severity key for the severity-stacked charts (the shared BarChart suppresses the x-charts legend). */
function SeverityLegend({ alsoApply }: { alsoApply?: [string, string][] }) {
  const { activeValue, isActive, toggle } = useLegendFilter("severity", alsoApply);
  return (
    <ChartLegend
      config={{ direction: "row", position: "above" }}
      items={SEVERITY_STACK.map((severity) => ({
        color: severity.color,
        value: severity.label,
        isSelected: activeValue === undefined ? undefined : isActive(severity.key),
      }))}
      onItemClick={(index) => {
        const severity = SEVERITY_STACK[index];
        if (severity) {
          toggle(severity.key);
        }
      }}
    />
  );
}

/**
 * What an empty overview says. A tenant with violations that a filter excluded
 * has not "no violations" — on a compliance product that reads as a clean bill
 * of health. Keyed off the same predicate the grid's own empty overlay uses
 * (see DefaultEmptyStateOverlay), so panel and grid agree on why a view is empty.
 */
function emptyStateMessage(omnisearch: string): string {
  return omnisearch.trim().length > 0
    ? "No violations match your current filters"
    : "No violations yet";
}

export function ViolationsOverviewPanel({ tenantId }: { tenantId: string }) {
  const theme = useTheme();
  const omnisearch = useFullOmnisearch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading } = useQuery(HARMONIZED_VIOLATION_STATS, {
    variables: { tenantId, search: omnisearch },
    skip: !tenantId,
  });

  const stats = data?.harmonizedViolationStats;

  const visibleViews = useMemo(
    () =>
      CHART_VIEW_KEYS.filter((key) => {
        if (key === "region") {
          return hasRealBuckets(stats?.byRegion ?? []);
        }
        if (key === "businessUnit") {
          return hasRealBuckets(stats?.byBusinessUnit ?? []);
        }
        return true;
      }),
    [stats?.byRegion, stats?.byBusinessUnit]
  );

  const paramView = searchParams.get(CHART_VIEW_PARAM);
  const selectedView: ChartViewKey =
    isChartViewKey(paramView) && visibleViews.includes(paramView) ? paramView : "quarter";

  const handleViewChange = (value: ChartViewKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(CHART_VIEW_PARAM, value);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <DashboardTile size={12}>
      <DashboardTile.Header
        title="Violations Overview"
        infoTooltip={
          <Typography variant="caption">
            Use the dropdown on the right to view your violations from different angles. Every view
            reflects the current search filters.
          </Typography>
        }
      >
        {stats && stats.total > 0 ? (
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ width: theme.spacing(32.5) }}>
              <InputLabel id="violations-chart-view">View by</InputLabel>
              <Select
                sx={{ height: theme.spacing(5) }}
                fullWidth
                labelId="violations-chart-view"
                label="View by"
                value={selectedView}
                onChange={(event) => handleViewChange(event.target.value as ChartViewKey)}
              >
                {visibleViews.map((key) => (
                  <MenuItem key={key} value={key}>
                    {chartVariations[key].selectLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        ) : null}
      </DashboardTile.Header>

      <DashboardTile.ContentArea sx={{ flex: 1 }}>
        {loading && !stats ? <OverviewSkeleton /> : null}
        {!loading && stats && stats.total === 0 ? (
          <Typography color="text.secondary">{emptyStateMessage(omnisearch)}</Typography>
        ) : null}
        {stats && stats.total > 0 ? (
          <Stack spacing={2}>
            {/* Equal 1fr tracks rather than a wrapping row of content-sized cards:
                the row has to span the tile's full width at every breakpoint. */}
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              <ViolationKeyMetric name="Total" value={stats.total} loading={false} />
              <ViolationKeyMetric
                name="High severity"
                value={stats.highSeverity}
                loading={false}
                info={<MetricInfoIcon explanation={SEVERITY_EXPLANATION} label="high severity" />}
              />
              <ViolationKeyMetric name="Unresolved" value={stats.unresolved} loading={false} />
              <ViolationKeyMetric
                name="Stale"
                value={stats.stale}
                loading={false}
                info={<MetricInfoIcon explanation={STALE_EXPLANATION} label="stale" />}
              />
              <ViolationKeyMetric
                name="Facilities affected"
                value={stats.facilitiesAffected}
                loading={false}
              />
            </Box>
            <SelectedChartView view={selectedView} stats={stats} />
          </Stack>
        ) : null}
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
}

/** Chart section heading in the waste graph style: subtitle1 + info icon. */
function ChartHeading({ view, children }: { view: ChartViewKey; children?: ReactNode }) {
  const variation = chartVariations[view];
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle1">{variation.label}</Typography>
        <MetricInfoIcon explanation={variation.explanation} label={variation.infoLabel} />
      </Stack>
      {children}
    </Stack>
  );
}

function SelectedChartView({ view, stats }: { view: ChartViewKey; stats: Stats }) {
  switch (view) {
    case "quarter":
      return <QuarterView buckets={stats.byQuarter} />;
    case "programOverTime":
      return <ProgramOverTimeView cells={stats.byProgramQuarter} />;
    case "facility":
      return (
        <RankedBarView
          view="facility"
          items={stats.topFacilities.map((f) => ({
            name: f.facilityName,
            count: f.count,
            // Filter by id, matching the facility popover — names are not unique.
            filterValue: f.facilityId,
            byProgramSeverity: f.byProgramSeverity,
          }))}
        />
      );
    case "region":
      return (
        <RankedBarView
          view="region"
          items={stats.byRegion.map((b) => ({
            name: b.label,
            count: b.count,
            // The "(No region)" sentinel isn't a filterable value.
            filterValue: b.key === SENTINEL_BUCKET_KEY ? undefined : b.label,
            byProgramSeverity: b.byProgramSeverity,
          }))}
        />
      );
    case "businessUnit":
      return (
        <RankedBarView
          view="businessUnit"
          items={stats.byBusinessUnit.map((b) => ({
            name: b.label,
            count: b.count,
            filterValue: b.key === SENTINEL_BUCKET_KEY ? undefined : b.label,
            byProgramSeverity: b.byProgramSeverity,
          }))}
        />
      );
    case "assignee":
      return <AssigneeSeverityView cells={stats.byAssigneeSeverity} />;
    case "programSeverity":
      return <ProgramSeverityView buckets={stats.byProgramSeverity} />;
  }
}

function QuarterView({ buckets }: { buckets: StatBucket[] }) {
  const theme = useTheme();
  const dataset = useMemo(
    () => buckets.map((q) => ({ quarter: q.label, count: q.count })),
    [buckets]
  );
  return (
    <Stack>
      <ChartHeading view="quarter" />
      <Box sx={{ height: theme.spacing(40) }}>
        {/* A line chart reads as a trend over time, matching the other Insights tabs. */}
        <LineChart
          dataset={dataset}
          series={[
            {
              dataKey: "count",
              color: blue[400],
              valueFormatter: (value) => (value === null ? "" : `Violations (${value})`),
            },
          ]}
          xAxis={[{ scaleType: "point", dataKey: "quarter" }]}
          yAxis={[{ scaleType: "linear", min: 0 }]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>
    </Stack>
  );
}

// Matches the byProgramQuarter cell order so the stack order is stable.
const PROGRAM_TIME_ORDER = [
  HarmonizedViolationProgram.Water,
  HarmonizedViolationProgram.Waste,
  HarmonizedViolationProgram.Air,
] as const;

function ProgramOverTimeView({ cells }: { cells: Stats["byProgramQuarter"] }) {
  const theme = useTheme();
  const { activeValue, isActive, toggle } = useLegendFilter("program");

  // One dataset row per quarter, preserving the API's chronological order.
  const dataset = useMemo(
    () =>
      pivotCells(
        cells,
        (cell) => cell.key,
        (cell) => ({ quarter: cell.label, ...ZERO_PROGRAM_COLUMNS }),
        (cell) => cell.program
      ),
    [cells]
  );

  return (
    <Stack>
      <ChartHeading view="programOverTime">
        {/* Clickable program key, replacing the chart's own inert legend. */}
        <ChartLegend
          config={{ direction: "row", position: "above" }}
          items={PROGRAM_TIME_ORDER.map((program) => {
            // Same icon + color as the program chips.
            const Icon = programIcon(program);
            const color = programChartColor(program);
            return {
              type: "icon" as const,
              icon: <Icon fontSize="small" sx={{ color }} />,
              color,
              value: PROGRAM_LABELS[program],
              isSelected: activeValue === undefined ? undefined : isActive(program),
            };
          })}
          onItemClick={(index) => {
            const program = PROGRAM_TIME_ORDER[index];
            if (program) {
              toggle(program);
            }
          }}
        />
      </ChartHeading>
      <Box sx={{ height: theme.spacing(40) }}>
        <LineChart
          dataset={dataset}
          series={PROGRAM_TIME_ORDER.map((program) => ({
            dataKey: program,
            label: PROGRAM_LABELS[program],
            // Same colors as the program chips' icons.
            color: programChartColor(program),
            // Stacked areas: the top edge is the quarterly total.
            area: true,
            stack: "programs",
            showMark: false,
            valueFormatter: (value: number | null) =>
              value === null ? "" : `${PROGRAM_LABELS[program]} (${value})`,
          }))}
          xAxis={[{ scaleType: "point", dataKey: "quarter" }]}
          yAxis={[{ scaleType: "linear", min: 0 }]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>
    </Stack>
  );
}

/**
 * A ranked bar chart with a numbered name legend beside it, cloned from the
 * waste "Highest waste stream shipments" view (HighestGeneratingWasteStreamsGraph).
 */
// Facility filters by id (names are not unique); region/BU filter by their
// value, scoped to unresolved because that is what those charts count.
const RANKED_VIEW_FILTERS = {
  facility: { key: "facilityId", alsoApply: [] as [string, string][] },
  region: { key: "region", alsoApply: APPLY_UNRESOLVED },
  businessUnit: { key: "businessUnit", alsoApply: APPLY_UNRESOLVED },
} as const;

function RankedBarView({
  view,
  items,
}: {
  view: "facility" | "region" | "businessUnit";
  // `filterValue` is what a legend click filters by; sentinel buckets omit it.
  items: {
    name: string;
    count: number;
    filterValue: string | undefined;
    byProgramSeverity: Stats["topFacilities"][number]["byProgramSeverity"];
  }[];
}) {
  const theme = useTheme();
  const viewFilter = RANKED_VIEW_FILTERS[view];
  const { activeValue, isActive, toggle } = useLegendFilter(viewFilter.key, viewFilter.alsoApply);
  const dataset = useMemo(
    () => items.map((item) => ({ name: item.name, count: item.count })),
    [items]
  );
  const handleLegendClick = (index: number) => {
    const item = items[index];
    if (item?.filterValue) {
      toggle(item.filterValue);
    }
  };
  return (
    <Stack>
      <ChartHeading view={view} />
      <Stack display="flex" direction="row" sx={{ height: theme.spacing(40) }}>
        <BarChart
          dataset={dataset.length ? dataset : [{ count: 0, name: "No data" }]}
          series={[
            {
              type: "bar",
              dataKey: "count",
              valueFormatter: (value) => ` ${formatNumber(value)} violations`,
            },
          ]}
          xAxis={[
            {
              scaleType: "band",
              dataKey: "name",
              categoryGapRatio: 0.4,
              colorMap: {
                type: "ordinal",
                colors: PriorityChartColorsPurple,
              },
            },
          ]}
          yAxis={[{ scaleType: "linear" }]}
          indexAxisLabelProperty={dataset.length ? "name" : undefined}
          axisTooltipContent={(dataIndex) => {
            const item = items[dataIndex];
            return item ? <ProgramSeverityMatrix cells={item.byProgramSeverity} /> : null;
          }}
          noData={items.length === 0}
          noDataMessage="No data"
        />
        <ChartLegend
          items={items.map((item, index) => ({
            color: PriorityChartColorsPurple[index],
            value: `${index + 1}. ${item.name}`,
            // Highlight the applied filter's entry; none applied leaves all full-strength.
            isSelected: activeValue === undefined ? undefined : isActive(item.filterValue ?? ""),
          }))}
          onItemClick={handleLegendClick}
          sx={{ flex: 1, maxWidth: "35%" }}
        />
      </Stack>
    </Stack>
  );
}

/** Stacked severity bars per effective assignee, with slanted x-axis labels so long names stay legible. */
function AssigneeSeverityView({ cells }: { cells: Stats["byAssigneeSeverity"] }) {
  const theme = useTheme();

  // One dataset row per assignee, preserving the API's count-desc order.
  const dataset = useMemo(
    () =>
      pivotCells(
        cells,
        (cell) => cell.key,
        (cell) => ({ assignee: cell.label, ...ZERO_SEVERITY_COLUMNS }),
        (cell) => cell.severity
      ),
    [cells]
  );

  return (
    <Stack>
      <ChartHeading view="assignee">
        <SeverityLegend alsoApply={APPLY_UNRESOLVED} />
      </ChartHeading>
      <Box sx={{ height: theme.spacing(45) }}>
        <BarChart
          dataset={dataset}
          series={SEVERITY_BAR_SERIES}
          xAxis={[
            {
              scaleType: "band",
              dataKey: "assignee",
              categoryGapRatio: 0.4,
              tickLabelStyle: { angle: -35, textAnchor: "end" },
            },
          ]}
          yAxis={[{ scaleType: "linear" }]}
          margin={{ bottom: 88 }}
          noData={cells.length === 0}
          noDataMessage="No data"
        />
      </Box>
    </Stack>
  );
}

function ProgramSeverityView({ buckets }: { buckets: Stats["byProgramSeverity"] }) {
  const theme = useTheme();

  // One dataset row per program, preserving the API's program order.
  const dataset = useMemo(
    () =>
      pivotCells(
        buckets,
        (cell) => cell.program,
        (cell) => ({ program: PROGRAM_LABELS[cell.program], ...ZERO_SEVERITY_COLUMNS }),
        (cell) => cell.severity
      ),
    [buckets]
  );

  return (
    <Stack>
      <ChartHeading view="programSeverity">
        <SeverityLegend />
      </ChartHeading>
      <Box sx={{ height: theme.spacing(40) }}>
        <BarChart
          dataset={dataset}
          series={SEVERITY_BAR_SERIES}
          xAxis={[{ scaleType: "band", dataKey: "program", categoryGapRatio: 0.4 }]}
          yAxis={[{ scaleType: "linear" }]}
          noData={buckets.length === 0}
          noDataMessage="No data"
        />
      </Box>
    </Stack>
  );
}

function OverviewSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ flex: 1 }} />
        ))}
      </Stack>
      <Skeleton variant="rounded" height={280} />
    </Stack>
  );
}
